import React from 'react';

export interface IconProps {
  size?: number | string;
  className?: string;
  color?: string;
  strokeWidth?: number;
  title?: string;
}

// Base Icon Component with accessibility
const BaseIcon: React.FC<IconProps & { children: React.ReactNode; viewBox?: string }> = ({
  size = 24,
  className = '',
  color = 'currentColor',
  strokeWidth = 2,
  title,
  children,
  viewBox = '0 0 24 24',
}) => (
  <svg
    width={size}
    height={size}
    viewBox={viewBox}
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    role="img"
    aria-labelledby={title ? `icon-${title}` : undefined}
  >
    {title && <title id={`icon-${title}`}>{title}</title>}
    {children}
  </svg>
);

// Planning Icons
export const TechLeadIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Tech Lead">
    <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" />
    <path d="M12 8v8" />
    <path d="M8 12h8" />
  </BaseIcon>
);

export const SystemRequirementsIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="System Requirements">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="9" y1="9" x2="15" y2="9" />
    <line x1="9" y1="13" x2="15" y2="13" />
    <line x1="9" y1="17" x2="11" y2="17" />
  </BaseIcon>
);

export const UserFlowPlannerIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="User Flow Planner">
    <circle cx="12" cy="5" r="2" />
    <path d="M12 7v4" />
    <path d="M8 11h8" />
    <circle cx="8" cy="14" r="2" />
    <circle cx="16" cy="14" r="2" />
    <path d="M8 16v2" />
    <path d="M16 16v2" />
  </BaseIcon>
);

export const DesignAgentIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Design Agent">
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </BaseIcon>
);

export const SystemArchitectIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="System Architect">
    <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" />
    <line x1="6" y1="18" x2="6.01" y2="18" />
  </BaseIcon>
);

export const ProjectManagerIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Project Manager">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <line x1="20" y1="8" x2="20" y2="14" />
    <line x1="23" y1="11" x2="17" y2="11" />
  </BaseIcon>
);

// Execution Icons
export const ImplementationIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Implementation">
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </BaseIcon>
);

export const QualityAssuranceIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Quality Assurance">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </BaseIcon>
);

export const VerificationIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Verification">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </BaseIcon>
);

export const TestingIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Testing">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </BaseIcon>
);

export const DeploymentIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Deployment">
    <path d="M4 12h16m-2-6l4 6-4 6M4 6l-4 6 4 6" />
  </BaseIcon>
);

// Status Icons
export const CheckIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Completed" strokeWidth={3}>
    <polyline points="20 6 9 17 4 12" />
  </BaseIcon>
);

export const SpinnerIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Running">
    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" />
  </BaseIcon>
);

export const CloseIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Close" strokeWidth={2.5}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </BaseIcon>
);

export const SearchIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Search">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </BaseIcon>
);

// Navigation Icons
export const ChevronRightIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} viewBox="0 0 12 12" title="Next">
    <path d="M4.5 2.5L8 6L4.5 9.5" />
  </BaseIcon>
);

export const ChevronDownIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Expand">
    <polyline points="6 9 12 15 18 9" />
  </BaseIcon>
);

export const PlanningIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Planning">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </BaseIcon>
);

export const ChevronIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} viewBox="0 0 12 12" title="Chevron">
    <path d="M4.5 2.5L8 6L4.5 9.5" />
  </BaseIcon>
);

export const RefreshIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Refresh">
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </BaseIcon>
);

export const SettingsIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Settings">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v6m0 6v6m4.22-13.22l4.24 4.24M1.54 9.96l4.24 4.24M20.46 14.04l4.24 4.24M1.54 14.04l4.24-4.24" />
  </BaseIcon>
);

// Specialized Agent Icons
export const BrowserIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Browser Verification">
    <rect x="2" y="4" width="20" height="16" rx="2" ry="2" />
    <path d="M2 8h20" />
    <circle cx="8" cy="6" r="1" />
    <circle cx="12" cy="6" r="1" />
    <circle cx="16" cy="6" r="1" />
  </BaseIcon>
);

export const DockerIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Docker">
    <path d="M8 13v-2h2v2zm0 4v-2h2v2zm4-4v-2h2v2zm0 4v-2h2v2zm4-4v-2h2v2zm0 4v-2h2v2z" />
    <path d="M3 5v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z" />
  </BaseIcon>
);

export const KubernetesIcon: React.FC<IconProps> = (props) => (
  <BaseIcon {...props} title="Kubernetes">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
  </BaseIcon>
);

// Icon mapping for agents
export const AGENT_ICONS = {
  'tech_lead_agent': TechLeadIcon,
  'system_requirements_agent': SystemRequirementsIcon,
  'user_flow_planner': UserFlowPlannerIcon,
  'design_agent': DesignAgentIcon,
  'system_architect': SystemArchitectIcon,
  'project_manager': ProjectManagerIcon,
  'implementation_agent': ImplementationIcon,
  'backend_quality_agent': QualityAssuranceIcon,
  'frontend_quality_agent': QualityAssuranceIcon,
  'browser_verification_agent': BrowserIcon,
  'verification_investigator': VerificationIcon,
  'decision_agent': CheckIcon,
  'test_agent': TestingIcon,
  'deployment_phase_6_agent': DeploymentIcon,
  'library_execution_agent': ImplementationIcon,
  'docker_health_check_agent': DockerIcon,
  'smart_fix_agent': SettingsIcon,
  'smart_command_recovery_agent': RefreshIcon,
} as const;
